import { computeListMarkers } from './computeListMarkers';
import { extractCastFromContentBlocks } from './extractCastFromContentBlocks';
import type { DocParts, FooterElement, HeaderElement } from '../../slate-api';
import {
  AlineTransforms,
  FooterType,
  HeaderType,
  isFooterElement,
  isHeaderElement,
  Queries,
} from '../../slate-api';
import { assignSectionPrimaryStyles } from '../helpers/computePrimaryStyle';
import { getIsEmptyCastParagraph } from '../helpers/getIsEmptyCastParagraph';
import type { CastRoot, CastSection, CastSectionVariant } from '../types';

export function extractCastFromAlineDoc(docParts: DocParts): CastRoot {
  const editor = AlineTransforms.createQueryEditorFromJson(docParts);
  const [[document], ...headerFooterEntries] = Queries.getActiveRootEntries(editor);

  /**
   * Number every list paragraph once, up front, using the editor's own
   * per-`listId` numbering, then look markers up per paragraph below.
   */
  const listMarkers = computeListMarkers(editor);

  const body: CastSection = {
    children: extractCastFromContentBlocks(document.children, listMarkers),
    sectionType: 'body',
    type: 'section',
  };
  const children: CastSection[] = [body];

  /**
   * Aline may surface multiple header/footer variants (Default, FirstPage,
   * Even). Capture every non-empty one with its variant; the serializer orders
   * them canonically and drops any that duplicate another's content.
   *
   * "Non-empty" means it has a table or a paragraph with text. Unlike the
   * body — where blank paragraphs are kept as insert anchors — a header or
   * footer holding only blank paragraphs renders nothing, so emitting it
   * would put an empty <header>/<footer> on every document.
   */
  for (const [rootElement] of headerFooterEntries) {
    const sectionChildren = extractCastFromContentBlocks(rootElement.children, listMarkers);
    const isAllBlank = sectionChildren.every(
      child => child.type === 'paragraph' && getIsEmptyCastParagraph(child)
    );
    if (isAllBlank) continue;

    if (isHeaderElement(rootElement)) {
      children.push({
        children: sectionChildren,
        sectionType: 'header',
        type: 'section',
        variant: headerVariant(rootElement.type),
      });
    } else if (isFooterElement(rootElement)) {
      children.push({
        children: sectionChildren,
        sectionType: 'footer',
        type: 'section',
        variant: footerVariant(rootElement.type),
      });
    }
  }

  const root: CastRoot = { children, type: 'root' };
  assignSectionPrimaryStyles(root);
  return root;
}

function headerVariant(type: HeaderElement['type']): CastSectionVariant {
  switch (type) {
    case HeaderType.FirstPage:
      return 'first-page';
    case HeaderType.Even:
      return 'even';
    case HeaderType.Default:
      return 'default';
  }
}

function footerVariant(type: FooterElement['type']): CastSectionVariant {
  switch (type) {
    case FooterType.FirstPage:
      return 'first-page';
    case FooterType.Even:
      return 'even';
    case FooterType.Default:
      return 'default';
  }
}
